import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import CountryCode from "../../data/countrycode.json";
import { contactEndPoints } from "../../services/apis";
import { apiConnector } from "../../services/apiConnector";
import toast from "react-hot-toast";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { BsChevronDown } from "react-icons/bs";

const ContactUsForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [projectType, setProjectType] = useState("Web App");

  // Country Dropdown State
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(CountryCode.find(c => c.code === "+91") || CountryCode[0]);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  useOnClickOutside(dropdownRef, () => setCountryDropdownOpen(false));

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = useForm({
    defaultValues: {
      countrycode: "+91"
    }
  });

  // Update logic when country is chosen
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setValue("countrycode", country.code);
    setCountryDropdownOpen(false);
    setSearchQuery("");
  };

  const submitContactForm = async (data) => {
    try {
      setLoading(true);
      const payload = {
        ...data,
        projectType,
      };
      await apiConnector("POST", contactEndPoints.CONTACT_US_API, payload);
      setLoading(false);
      setSubmittedEmail(data.email);
      setSubmitted(true);
      toast.success("Success! We will get back to you shortly");
    } catch (error) {
      toast.error("Something went wrong");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({
        email: "",
        firstName: "",
        lastName: "",
        message: "",
        phoneNo: "",
      });
      setValue("countrycode", "+91");
      setSelectedCountry(CountryCode.find(c => c.code === "+91") || CountryCode[0]);
    }
  }, [reset, isSubmitSuccessful, setValue]);

  const filteredCountries = CountryCode.filter(c => 
    c.country.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.code.includes(searchQuery)
  );

  if (submitted) {
    return (
      <div className="flex flex-col items-center text-center py-10 px-4 animate-fadeIn">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 relative"
             style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1.5px solid rgba(34, 197, 94, 0.3)' }}>
          <div className="absolute inset-0 rounded-full bg-green-500/20 animate-ping opacity-40" />
          <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h3 className="text-2xl font-black mb-3" style={{ color: 'var(--text-primary)' }}>
          Inquiry Received!
        </h3>
        <p className="text-sm leading-relaxed max-w-md mb-8" style={{ color: 'var(--text-secondary)' }}>
          Thank you for reaching out. We have sent a confirmation to <span className="font-semibold text-slate-300">{submittedEmail}</span>. 
          Our engineering team is already reviewing your scope, and we'll contact you in less than 24 hours.
        </p>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setSubmitted(false)}
            className="spatial-btn-secondary !text-xs !py-2.5 !px-5"
          >
            Send Another Message
          </button>
          <a href="/portfolio" className="spatial-btn-tech group !text-xs !py-2.5 !px-5 rounded-lg font-bold relative overflow-hidden">
            <span className="spatial-btn-tech-bg" />
            <span className="spatial-btn-tech-shimmer" />
            <span className="relative z-10 flex items-center gap-1.5">
              View Our Work
            </span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(submitContactForm)}>
      {/* Name Row */}
      <div className="flex flex-col gap-5 lg:flex-row">
        <div className="flex flex-col gap-1.5 lg:w-1/2">
          <label htmlFor="firstName" className="form-label-apple">
            First Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            placeholder="Enter first name"
            className="form-input-apple"
            {...register("firstName", { required: true })}
          />
          {errors.firstName && (
            <span className="text-xs text-red-400 font-medium">Please enter your name.</span>
          )}
        </div>
        <div className="flex flex-col gap-1.5 lg:w-1/2">
          <label htmlFor="lastName" className="form-label-apple">
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            placeholder="Enter last name"
            className="form-input-apple"
            {...register("lastName")}
          />
        </div>
      </div>

      {/* Email */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="email" className="form-label-apple">
          Email Address <span className="text-red-400">*</span>
        </label>
        <input
          type="email"
          id="email"
          placeholder="Enter email address"
          className="form-input-apple"
          {...register("email", {
            required: "Please enter your email address.",
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
              message: "Please enter a valid email address."
            }
          })}
        />
        {errors.email && (
          <span className="text-xs text-red-400 font-medium">{errors.email.message}</span>
        )}
      </div>

      {/* Phone */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="phonenumber" className="form-label-apple">
          Phone Number <span className="text-red-400">*</span>
        </label>
        <div className="flex gap-3 w-full relative">
          
          {/* Custom Country Dropdown */}
          <div className="relative w-[100px] min-w-[100px] flex-shrink-0" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
              className="form-input-apple w-full !px-3 flex items-center justify-between text-xs h-full"
              style={{ paddingBottom: '0.65rem', paddingTop: '0.65rem' }}
            >
              <div className="flex items-center gap-1.5 truncate">
                <span className="font-semibold">{selectedCountry?.code}</span>
              </div>
              <BsChevronDown className={`transition-transform duration-300 ${countryDropdownOpen ? 'rotate-180': ''}`} style={{color: 'var(--text-tertiary)'}} />
            </button>
            
            {/* Hidden Input for form submission */}
            <input type="hidden" {...register("countrycode", { required: true })} />

            {/* Dropdown Panel */}
            {countryDropdownOpen && (
              <div 
                className="absolute left-0 mt-2 w-[220px] rounded-xl shadow-xl z-50 overflow-hidden flex flex-col"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', top: '100%' }}
              >
                <div className="p-2 border-b border-white/10">
                  <input 
                    type="text"
                    placeholder="Search country..."
                    className="w-full bg-slate-900/50 rounded-lg px-3 py-2 text-xs outline-none"
                    style={{ color: 'var(--text-primary)', border: '1px solid var(--border-color)'}}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>
                <ul className="max-h-[220px] overflow-y-auto w-full custom-scrollbar">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((ele, i) => (
                      <li 
                        key={i} 
                        onClick={() => handleCountrySelect(ele)}
                        className="px-3 py-2.5 text-xs cursor-pointer flex justify-between items-center transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <span className="truncate pr-4">{ele.country}</span>
                        <span className="font-medium flex-shrink-0" style={{ color: 'var(--text-primary)' }}>{ele.code}</span>
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-4 text-xs text-center text-slate-500">No results found</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          <input
            type="tel"
            id="phonenumber"
            placeholder="12345 67890"
            className="form-input-apple flex-1 min-w-0"
            {...register("phoneNo", {
              required: { value: true, message: "Please enter your phone number." },
              maxLength: { value: 12, message: "Invalid phone number" },
              minLength: { value: 10, message: "Invalid phone number" },
            })}
          />
        </div>
        {errors.phoneNo && (
          <span className="text-xs text-red-400 font-medium">{errors.phoneNo.message}</span>
        )}
      </div>

      {/* Project Type Selector */}
      <div className="flex flex-col gap-2">
        <label className="form-label-apple">What are you looking to build?</label>
        <div className="flex flex-wrap gap-2">
          {["Web App", "Mobile App", "SaaS Product", "AI Integration", "Email Hosting", "Other"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setProjectType(t)}
              className="text-xs font-semibold px-4 py-2.5 rounded-xl border transition-all duration-300"
              style={{
                borderColor: projectType === t ? 'var(--accent)' : 'var(--border-color)',
                background: projectType === t ? 'var(--accent-soft)' : 'var(--bg-primary)',
                color: projectType === t ? 'var(--accent)' : 'var(--text-secondary)'
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="message" className="form-label-apple">
          Message <span className="text-red-400">*</span>
        </label>
        <textarea
          id="message"
          cols="30"
          rows="5"
          placeholder="Tell us about your project..."
          className="form-input-apple resize-none"
          {...register("message", { required: true })}
        />
        {errors.message && (
          <span className="text-xs text-red-400 font-medium">Please enter your message.</span>
        )}
      </div>

      {/* Submit */}
      <button
        disabled={loading}
        type="submit"
        className={`btn-primary w-full justify-center mt-2
          ${loading ? 'opacity-60 cursor-not-allowed' : ''}
          disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Sending...
          </span>
        ) : (
          "Send Message"
        )}
      </button>
    </form>
  );
};

export default ContactUsForm;